import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  Shield, 
  Globe, 
  Zap, 
  FileText, 
  Target,
  TrendingUp,
  Users,
  ArrowRight,
  CheckCircle,
  Sun,
  Moon
} from 'lucide-react';

const LandingPage = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  
  const pillars = [
    { icon: Target, name: language === 'tr' ? 'Dijital Strateji' : 'Digital Strategy', weight: '18%' },
    { icon: Shield, name: language === 'tr' ? 'Teknoloji Altyapısı' : 'Technology Infrastructure', weight: '17%' },
    { icon: BarChart3, name: language === 'tr' ? 'Veri & AI' : 'Data & AI', weight: '18%' },
    { icon: Zap, name: language === 'tr' ? 'Süreç Otomasyonu' : 'Process Automation', weight: '16%' },
    { icon: Users, name: language === 'tr' ? 'Müşteri Deneyimi' : 'Customer Experience', weight: '16%' },
    { icon: TrendingUp, name: language === 'tr' ? 'Organizasyon Kültürü' : 'Organization Culture', weight: '15%' },
  ];
  
  const features = [
    {
      icon: BarChart3,
      title: language === 'tr' ? '320+ Değerlendirme Sorusu' : '320+ Assessment Questions',
      description: language === 'tr' 
        ? 'Bilimsel metodoloji ile hazırlanmış kapsamlı soru seti'
        : 'Comprehensive question set prepared with scientific methodology'
    },
    {
      icon: Zap,
      title: language === 'tr' ? 'AI Destekli Raporlar' : 'AI-Powered Reports',
      description: language === 'tr'
        ? 'Dünya standartlarında profesyonel danışmanlık raporları'
        : 'World-class professional consulting reports'
    },
    {
      icon: Globe,
      title: language === 'tr' ? 'Global Benchmark' : 'Global Benchmarking',
      description: language === 'tr'
        ? 'Sektör ve bölge bazlı karşılaştırma analizi'
        : 'Industry and region-based comparison analysis'
    },
    {
      icon: FileText,
      title: language === 'tr' ? 'White-Label Raporlar' : 'White-Label Reports',
      description: language === 'tr'
        ? 'Kendi markanızla özelleştirilmiş PDF raporlar'
        : 'Customized PDF reports with your brand'
    },
  ];
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">GDMII</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/pricing">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white font-medium hidden sm:inline-flex"
                  data-testid="pricing-link"
                >
                  {language === 'tr' ? 'Fiyatlandırma' : 'Pricing'}
                </Button>
              </Link>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="text-slate-400 hover:text-white"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-400 hover:text-white font-medium"
                data-testid="language-toggle-landing"
              >
                {language === 'tr' ? 'EN' : 'TR'}
              </Button>
              
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="dashboard-btn">
                    {t('dashboard')}
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="login-btn">
                    {t('login')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-8">
              <Zap className="w-4 h-4 mr-2" />
              {language === 'tr' ? 'AI Destekli Dijital Dönüşüm Platformu' : 'AI-Powered Digital Transformation Platform'}
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                {language === 'tr' ? 'Dijital Olgunluk' : 'Digital Maturity'}
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {language === 'tr' ? 'Değerlendirme Platformu' : 'Intelligence Platform'}
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={isAuthenticated ? '/dashboard' : '/auth'}>
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6 h-auto"
                  data-testid="hero-start-btn"
                >
                  {t('startAssessment')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              
              <Button 
                size="lg" 
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8 py-6 h-auto"
                data-testid="hero-learn-btn"
              >
                {t('learnMore')}
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            {[
              { value: '320+', label: language === 'tr' ? 'Değerlendirme Sorusu' : 'Assessment Questions' },
              { value: '6', label: language === 'tr' ? 'Stratejik Alan' : 'Strategic Pillars' },
              { value: 'AI', label: language === 'tr' ? 'Destekli Raporlar' : 'Powered Reports' },
              { value: 'Global', label: language === 'tr' ? 'Benchmark Verisi' : 'Benchmark Data' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* 6 Pillars Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {language === 'tr' ? '6 Pillar Bilimsel Çerçeve' : '6-Pillar Scientific Framework'}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {language === 'tr' 
                ? 'GDMII, dijital olgunluğu 6 stratejik alanda ölçen bilimsel bir metodoloji kullanır.'
                : 'GDMII uses a scientific methodology that measures digital maturity across 6 strategic pillars.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, i) => (
              <div 
                key={i}
                className="p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400 group-hover:bg-blue-500/20 transition-colors">
                    <pillar.icon className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">{pillar.weight}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{pillar.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {language === 'tr' ? 'Platform Özellikleri' : 'Platform Features'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="flex items-start space-x-4 p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors"
              >
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 text-blue-400 flex-shrink-0">
                  <feature.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Report Types Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {language === 'tr' ? 'Kurumsal Seviye Raporlar' : 'Enterprise-Grade Reports'}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {language === 'tr'
                ? 'Dünya standartlarında AI destekli stratejik dönüşüm raporları'
                : 'World-class AI-powered strategic transformation reports'}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              language === 'tr' ? 'Yönetici Özeti' : 'Executive Summary',
              language === 'tr' ? '6 Alan Analizi' : '6-Pillar Analysis',
              language === 'tr' ? 'Boşluk Analizi' : 'Gap Analysis',
              language === 'tr' ? 'AI Hazırlık Analizi' : 'AI Readiness',
              language === 'tr' ? 'Risk Analizi' : 'Risk Analysis',
              language === 'tr' ? 'Dönüşüm Yol Haritası' : 'Transformation Roadmap',
              language === 'tr' ? 'ROI Simülasyonu' : 'ROI Simulation',
              language === 'tr' ? 'Öncelik Matrisi' : 'Priority Matrix',
            ].map((item, i) => (
              <div key={i} className="flex items-center space-x-3 p-4 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {language === 'tr' 
              ? 'Dijital Dönüşüm Yolculuğunuza Başlayın'
              : 'Start Your Digital Transformation Journey'}
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            {language === 'tr'
              ? 'Şirketinizin dijital olgunluk seviyesini ölçün ve stratejik öneriler alın.'
              : 'Measure your company\'s digital maturity level and get strategic recommendations.'}
          </p>
          <Link to={isAuthenticated ? '/dashboard' : '/auth'}>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-10 py-6 h-auto"
              data-testid="cta-btn"
            >
              {language === 'tr' ? 'Ücretsiz Başlayın' : 'Start Free'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 sm:mb-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold">GDMII</span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 GDMII Platform. {language === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
